import { useHandleHttpError } from '../../../../../lib/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { arrayBufferToBase64 } from '../../../../../services/utils';
import type { UploadCertificateWithPasswordProps } from './types';
import * as Yup from 'yup';
import { BulmaType, ButtonProps, Form, Input, TaskPanel, useToast } from '@easylife365/react';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useUploadSamlCertificate } from '../../../../../api/app.api';
import { UploadSamlCertificateRequest } from '../../../../../api/app.types';

const initialValues: UploadSamlCertificateRequest = {
  certificateBase64: '',
  password: '',
};

const allowedExtensions = ['.pfx'];

const UploadCertificateWithPassword: React.FC<UploadCertificateWithPasswordProps> = ({ app, isOpen, close }) => {
  const { t } = useTranslation('addCertificatePanel');
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        certificateBase64: Yup.string().required(t('validation.certificateRequired')),
        password: Yup.string().required(t('validation.passwordRequired')),
      }),
    [t]
  );
  const { toastError } = useToast();
  const handleHttpError = useHandleHttpError();
  const { mutateAsync: uploadSamlCertificate } = useUploadSamlCertificate(app.resourceType);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      toastError(t('toasts.invalidFile'), t('toasts.invalidFilePfx'));
      return;
    }

    const fileExtension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    if (fileExtension !== '.pfx') {
      toastError(t('toasts.invalidType'), t('toasts.invalidTypePfx'));
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(selectedFile);

    reader.onload = async () => {
      if (reader.result instanceof ArrayBuffer) {
        try {
          const uint8Array = new Uint8Array(reader.result);
          const base64Pfx = arrayBufferToBase64(uint8Array);
          setFieldValue('certificateBase64', base64Pfx);
        } catch (error) {
          console.error('Error processing the file:', error);
          toastError(t('toasts.processingError'), t('toasts.processingErrorMessage'));
        }
      } else {
        toastError(t('toasts.invalidFormat'), t('toasts.invalidFormatMessage'));
      }
    };

    reader.onerror = () => {
      console.error('Error reading the file.');
      toastError(t('toasts.readError'), t('toasts.readErrorMessage'));
    };
  };

  const onSubmit = async (values: UploadSamlCertificateRequest) => {
    try {
      await uploadSamlCertificate({
        id: app.id,
        request: values,
      });
      close();
    } catch (error) {
      handleHttpError(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ isSubmitting, isValid, setFieldValue, handleSubmit }) => {
        const ctaButtons: ButtonProps[] = [
          {
            bulmaType: BulmaType.Primary,
            disabled: !isValid || isSubmitting,
            text: t('buttons.upload'),
            icon: faWandMagicSparkles,
            loading: isSubmitting,
            type: 'submit',
          },
        ];

        return (
          <form onSubmit={handleSubmit}>
            <TaskPanel title={t('uploadPrivate.title')} isVisible={isOpen} ctaButtons={ctaButtons} onCancel={close}>
              <Form transparent={true}>
                <Input
                  formLabel={t('fields.certificate')}
                  helpMessage={t('uploadPrivate.help')}
                  required
                  type="file"
                  accept={allowedExtensions.join(',')}
                  onChange={(e) => handleFileChange(e, setFieldValue)}
                />
                <Input
                  formLabel={t('fields.password')}
                  helpMessage={t('fields.passwordHelp')}
                  name="password"
                  placeholder={t('fields.passwordPlaceholder')}
                />
              </Form>
            </TaskPanel>
          </form>
        );
      }}
    </Formik>
  );
};

export default UploadCertificateWithPassword;
